import { useMemo, useState, type ComponentPropsWithoutRef, type ReactNode } from "react";
import remarkGfm from "remark-gfm";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { CodeLibraryMunicipality, CompareResult } from "@/api/code_library_compare";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SafeMarkdown } from "@/components/markdown/SafeMarkdown";
import { citationBadgeClassName, citationUrlTransform } from "@/components/chat/citationMarkdown";
import { normalizeCitationLinks, resolveCitationKeyFromHref } from "@/components/chat/citationUtils";
import AiDisclaimer from "@/components/AiDisclaimer";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";
import { cn } from "@/lib/utils";
import {
  buildComparisonSources,
  linkSourceMarkers,
  SIDE_STYLES,
  type ComparisonSide,
  type ComparisonSource,
} from "@/components/code_library/comparisonSources";

const PROSE_CLASS =
  "text-sm leading-6 space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_h3]:font-semibold";

type OpenSource = (number: number) => void;

// Labels run from the code's title down to the section, so the last part is what tells
// sources apart in a narrow list. The full label stays in the tooltip and the source panel.
const shortSourceLabel = (label: string) => label.split(" - ").pop()?.trim() || label;

/** A community's name in its comparison color. */
export function CommunityChip({
  side,
  municipality,
  className,
}: {
  side: ComparisonSide;
  municipality: CodeLibraryMunicipality;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-sm font-semibold",
        SIDE_STYLES[side].chip,
        className,
      )}
    >
      {formatMunicipalityLabel(municipality)}
    </span>
  );
}

/**
 * Renders citation links as badges in the cited source's community color. resolve maps a
 * marker's key (a source number in the summary, a label in an answer) to its source.
 */
function sourceBadgeComponents(resolve: (key: string) => ComparisonSource | undefined, onOpenSource: OpenSource) {
  return {
    a: ({ href, children, ...props }: ComponentPropsWithoutRef<"a">) => {
      if (!href?.startsWith("citation:")) {
        return (
          <a href={href} {...props}>
            {children}
          </a>
        );
      }
      const source = resolve(href.slice("citation:".length).trim());
      if (!source) {
        return (
          <span className={`${citationBadgeClassName} cursor-default opacity-60`} aria-label="Unresolved citation">
            ?
          </span>
        );
      }
      return (
        <button
          type="button"
          onClick={() => onOpenSource(source.number)}
          className={SIDE_STYLES[source.side].badge}
          aria-label={`Open citation ${source.number}`}
          title={source.label}
        >
          {source.number}
        </button>
      );
    },
  };
}

/** Text whose [n] markers become badges that open source n. */
function SourcedText({
  text,
  sources,
  onOpenSource,
  inline = false,
}: {
  text: string;
  sources: ComparisonSource[];
  onOpenSource: OpenSource;
  inline?: boolean;
}) {
  const components = useMemo(() => {
    const base = sourceBadgeComponents((key) => sources[Number(key) - 1], onOpenSource);
    return inline ? { ...base, p: ({ children }: { children?: ReactNode }) => <>{children}</> } : base;
  }, [sources, onOpenSource, inline]);

  return (
    <SafeMarkdown remarkPlugins={[remarkGfm]} components={components} urlTransform={citationUrlTransform}>
      {linkSourceMarkers(text)}
    </SafeMarkdown>
  );
}

function SourcedList({
  title,
  items,
  sources,
  onOpenSource,
}: {
  title: string;
  items: string[];
  sources: ComparisonSource[];
  onOpenSource: OpenSource;
}) {
  if (!items.length) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold">{title}</h3>
      <ul className="list-disc space-y-1.5 pl-5 text-sm leading-6">
        {items.map((item, index) => (
          <li key={`${index}-${item}`}>
            <SourcedText text={item} sources={sources} onOpenSource={onOpenSource} inline />
          </li>
        ))}
      </ul>
    </section>
  );
}

/** One community's answer: its badges use the comparison-wide numbers. */
function CommunityAnswer({
  municipality,
  side,
  answer,
  sources,
  onOpenSource,
}: {
  municipality: CodeLibraryMunicipality;
  side: ComparisonSide;
  answer: string;
  sources: ComparisonSource[];
  onOpenSource: OpenSource;
}) {
  const sideSources = useMemo(() => sources.filter((source) => source.side === side), [sources, side]);
  const content = useMemo(
    () => normalizeCitationLinks(answer, sideSources.map((source) => ({ [source.label]: source.excerpt }))),
    [answer, sideSources],
  );
  const components = useMemo(() => {
    // An answer cites by label; when two sources share one, the marker opens the first.
    const sourceByLabel = new Map<string, ComparisonSource>();
    for (const source of sideSources) {
      if (!sourceByLabel.has(source.label)) sourceByLabel.set(source.label, source);
    }
    return sourceBadgeComponents(
      (key) => sourceByLabel.get(resolveCitationKeyFromHref(key, sourceByLabel.keys())),
      onOpenSource,
    );
  }, [sideSources, onOpenSource]);
  const title = formatMunicipalityLabel(municipality);

  return (
    <section className="flex min-w-0 flex-col gap-4 rounded-lg border bg-card p-4 sm:p-5" aria-label={title}>
      <h3>
        <CommunityChip side={side} municipality={municipality} className="text-base" />
      </h3>
      <div className={PROSE_CLASS}>
        <SafeMarkdown remarkPlugins={[remarkGfm]} components={components} urlTransform={citationUrlTransform}>
          {content}
        </SafeMarkdown>
      </div>
      <div className="mt-auto space-y-1 border-t pt-3">
        <h4 className="text-xs font-medium text-muted-foreground">
          {sideSources.length ? `Sources (${sideSources.length})` : "No sources cited"}
        </h4>
        {sideSources.length > 0 && (
          <ul className="space-y-0.5">
            {sideSources.map((source) => (
              <li key={source.number}>
                <button
                  type="button"
                  onClick={() => onOpenSource(source.number)}
                  className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className={cn(SIDE_STYLES[side].badge, "mx-0 shrink-0 translate-y-0")} aria-hidden>
                    {source.number}
                  </span>
                  <span className="min-w-0 truncate" title={source.label}>
                    {shortSourceLabel(source.label)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function SourcePanel({
  sources,
  openNumber,
  onOpenChange,
  onOpenSource,
}: {
  sources: ComparisonSource[];
  openNumber: number | null;
  onOpenChange: (open: boolean) => void;
  onOpenSource: OpenSource;
}) {
  const source = sources.find((item) => item.number === openNumber);

  return (
    <Sheet open={source != null} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        {source && (
          <>
            <SheetHeader className="space-y-2 border-b p-5 pr-12 text-left">
              <SheetDescription className="flex flex-wrap items-center gap-2">
                <CommunityChip side={source.side} municipality={source.municipality} />
                <span>
                  Source {source.number} of {sources.length}
                </span>
              </SheetDescription>
              <SheetTitle className="text-base leading-6">{source.label}</SheetTitle>
            </SheetHeader>
            <div className="ordinal-scrollbar min-h-0 flex-1 overflow-auto p-5">
              <div className={`${PROSE_CLASS} [&_h1]:font-semibold [&_h2]:font-semibold`}>
                <SafeMarkdown remarkPlugins={[remarkGfm]}>{source.excerpt || "This source has no excerpt."}</SafeMarkdown>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 border-t p-3">
              <Button
                variant="outline"
                size="sm"
                disabled={source.number <= 1}
                onClick={() => onOpenSource(source.number - 1)}
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Previous source
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={source.number >= sources.length}
                onClick={() => onOpenSource(source.number + 1)}
              >
                Next source
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export function ComparisonResult({ result }: { result: CompareResult }) {
  const sources = useMemo(() => buildComparisonSources(result), [result]);
  const [openNumber, setOpenNumber] = useState<number | null>(null);
  const summary = result.summary;
  // Comparisons saved before cited summaries may hold their text only in the markdown content,
  // which already includes the lists.
  const legacyContent = summary.summary ? undefined : summary.content;
  const summaryText = summary.summary || legacyContent || "No comparison was generated.";

  return (
    <div className="space-y-6">
      <section className="space-y-5 rounded-lg border bg-card p-4 sm:p-5" aria-labelledby="comparison-overview-heading">
        <h2 id="comparison-overview-heading" className="text-lg font-semibold">
          How they compare
        </h2>
        <div className={PROSE_CLASS}>
          <SourcedText text={summaryText} sources={sources} onOpenSource={setOpenNumber} />
        </div>
        {!legacyContent && (
          <div className="grid gap-5 md:grid-cols-2">
            <SourcedList
              title="Differences"
              items={summary.differences ?? []}
              sources={sources}
              onOpenSource={setOpenNumber}
            />
            <SourcedList
              title="Similarities"
              items={summary.similarities ?? []}
              sources={sources}
              onOpenSource={setOpenNumber}
            />
          </div>
        )}
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <CommunityAnswer
          municipality={result.home_municipality}
          side="home"
          answer={result.home.answer || "No answer was generated."}
          sources={sources}
          onOpenSource={setOpenNumber}
        />
        <CommunityAnswer
          municipality={result.comparison_municipality}
          side="comparison"
          answer={result.comparison.answer || "No answer was generated."}
          sources={sources}
          onOpenSource={setOpenNumber}
        />
      </div>

      <AiDisclaimer className="text-center" />

      <SourcePanel
        sources={sources}
        openNumber={openNumber}
        onOpenChange={(open) => !open && setOpenNumber(null)}
        onOpenSource={setOpenNumber}
      />
    </div>
  );
}
